// =============================================================================
// colataService — UNICO file che conosce tabelle, view e funzioni SQL del modulo Colata.
// La UI usa soltanto le chiavi "entita" (es. 'zone', 'reparti') e mai i nomi SQL.
// Tutte le liste passano da .range() con count: nessun select senza limite.
// =============================================================================
import { supabase } from '../supabase';

// ─── Errori ──────────────────────────────────────────────────────────────────

export class ColataError extends Error {
  constructor({ code, message, campo = null, detail = null, originale = null }) {
    super(message);
    this.name = 'ColataError';
    this.code = code;
    this.campo = campo;
    this.detail = detail;
    this.originale = originale;
  }
}

// Codici applicativi (message dell'eccezione SQL) -> messaggio italiano
const MESSAGGI_CODICE = {
  NON_AUTORIZZATO: 'Non hai i permessi per eseguire questa operazione.',
  SOLA_LETTURA: 'Sola lettura: non hai i permessi per modificare questi dati.',
  IDEMPOTENCY_KEY_OBBLIGATORIA: 'Richiesta non valida: chiave di idempotenza mancante.',
  IDEMPOTENCY_CONFLITTO: 'Questa richiesta è già stata usata per un\'altra operazione.',
  QTA_NON_VALIDA: 'Quantità non valida per questa unità di misura.',
  QTA_INSUFFICIENTE: 'Quantità insufficiente in magazzino.',
  QTA_PROTETTA: 'La giacenza si modifica solo registrando un movimento.',
  ARTICOLO_NON_TROVATO: 'Articolo non trovato.',
  ARTICOLO_DISATTIVO: 'L\'articolo è disattivato.',
  TIPO_NON_VALIDO: 'Tipo di intervento non valido o disattivato.',
  ZONA_NON_TROVATA: 'Zona non trovata o disattivata.',
  COMPONENTE_NON_VALIDO: 'Il componente non appartiene a questa zona o a questo impianto, oppure è disattivato.',
  IMPIANTO_NON_VALIDO: 'L\'impianto non appartiene a questa zona o è disattivato.',
  IMPIANTO_OBBLIGATORIO: 'Seleziona l\'impianto: questa zona ha degli impianti.',
  LINEA_NON_VALIDA: 'La linea non appartiene a questa zona o è disattivata.',
  CAUSA_NON_VALIDA: 'Causa non valida o disattivata.',
  DATA_NON_VALIDA: 'Data non valida.',
  CODICE_NON_VALIDO: 'Codice non valido: 4-24 caratteri, solo lettere, numeri e trattino.',
  CODICE_DUPLICATO: 'Esiste già un articolo con questo codice.',
  NOME_OBBLIGATORIO: 'Il nome è obbligatorio.',
  RIFERIMENTO_NON_VALIDO: 'Uno dei valori selezionati non esiste più.',
  ATTRIBUTI_NON_VALIDI: 'Uno o più campi tecnici non sono validi.',
  MOTIVO_OBBLIGATORIO: 'Il motivo è obbligatorio.',
  MOVIMENTO_NON_TROVATO: 'Movimento non trovato.',
  MOVIMENTO_NON_STORNABILE: 'Questo movimento non può essere stornato.',
  MOVIMENTO_GIA_STORNATO: 'Questo movimento è già stato stornato.',
  MOVIMENTO_IMMUTABILE: 'I movimenti non si modificano né si cancellano: usa lo storno.',
  INTERVENTO_IMMUTABILE: 'Gli interventi non si modificano né si cancellano.',
  INTERVENTO_NON_TROVATO: 'Intervento non trovato.',
  INTERVENTO_ANNULLATO: 'Questo intervento è già stato annullato.',
  ORDINE_NON_VALIDO: 'L\'ordine selezionato non è valido per questo articolo.',
  ORDINE_CHIUSO: 'L\'ordine è già chiuso.',
  ORDINE_CON_RICEZIONI: 'L\'ordine ha già delle ricezioni: non si può annullare né cambiare articolo o tipo. Usa "Segna come ricevuto".',
  RICEZIONE_IMMUTABILE: 'Le ricezioni degli ordini non si modificano né si cancellano.',
  RECORD_IN_USO: 'Impossibile eliminare: l\'elemento è collegato ad altri dati. Disattivalo invece di eliminarlo.',
  RECORD_NON_TROVATO: 'Elemento non trovato (forse è già stato eliminato).',
  TABELLA_NON_VALIDA: 'Operazione non consentita su questa tabella.',
  KEY_IMMUTABILE: 'La chiave non è modificabile dopo la creazione.',
  CAMPO_IN_USO: 'Il campo è già usato da alcuni dati: non si può cambiare tipo o rimuovere opzioni in uso.',
  TIPO_IN_USO: 'Il comportamento non è modificabile: esistono già interventi di questo tipo.',
  UNITA_BLOCCATA: 'L\'unità di misura non si cambia se esistono già movimenti.',
  NESSUN_RECORD: 'Nessun elemento aggiornato: controlla i permessi o riprova.',
  SESSIONE_SCADUTA: 'Sessione scaduta: effettua di nuovo il login.',
  RETE: 'Connessione non disponibile. Riprova tra poco.',
};

const MSG_CHIAVE = 'La chiave può contenere solo lettere minuscole, numeri e _.';

// Vincoli Postgres (nome) -> campo del form + messaggio (nomi come nelle migrazioni colata_02..04)
const VINCOLI = {
  colata_reparti_key_uq:           { campo: 'key',    messaggio: 'Esiste già un reparto con questa chiave.' },
  colata_campi_key_uq:             { campo: 'key',    messaggio: 'Esiste già un campo con questa chiave in questo reparto.' },
  colata_cause_key_uq:             { campo: 'key',    messaggio: 'Esiste già una causa con questa chiave.' },
  colata_tipi_intervento_key_uq:   { campo: 'key',    messaggio: 'Esiste già un tipo di intervento con questa chiave.' },
  colata_magazzini_key_uq:         { campo: 'key',    messaggio: 'Esiste già un magazzino con questa chiave.' },
  colata_unita_misura_key_uq:      { campo: 'key',    messaggio: 'Esiste già un\'unità di misura con questa chiave.' },
  colata_ubicazioni_codice_uq:     { campo: 'codice', messaggio: 'Esiste già un\'ubicazione con questo codice in questo magazzino.' },
  colata_zone_codice_uq:           { campo: 'codice', messaggio: 'Esiste già una zona con questo codice.' },
  colata_impianti_nome_uq:         { campo: 'nome',   messaggio: 'Esiste già un impianto con questo nome in questa zona.' },
  colata_linee_nome_uq:            { campo: 'nome',   messaggio: 'Esiste già una linea con questo nome in questa zona.' },
  colata_articoli_codice_uq:       { campo: 'codice', messaggio: 'Esiste già un articolo con questo codice.' },
  colata_articoli_codice_ci_uq:    { campo: 'codice', messaggio: 'Esiste già un articolo con questo codice (le maiuscole non contano).' },
  colata_compatibilita_coppia_uq:  { campo: null,     messaggio: 'Questo articolo è già associato a questo componente.' },
  colata_compatibilita_primario_uq:{ campo: null,     messaggio: 'Questo componente ha già un articolo primario.' },
  colata_reparti_key_ck:           { campo: 'key',    messaggio: MSG_CHIAVE },
  colata_campi_key_ck:             { campo: 'key',    messaggio: MSG_CHIAVE },
  colata_cause_key_ck:             { campo: 'key',    messaggio: MSG_CHIAVE },
  colata_tipi_intervento_key_ck:   { campo: 'key',    messaggio: MSG_CHIAVE },
  colata_magazzini_key_ck:         { campo: 'key',    messaggio: MSG_CHIAVE },
  colata_unita_misura_key_ck:      { campo: 'key',    messaggio: MSG_CHIAVE },
  colata_reparti_label_ck:         { campo: 'label',  messaggio: 'Obbligatorio' },
  colata_campi_label_ck:           { campo: 'label',  messaggio: 'Obbligatorio' },
  colata_cause_label_ck:           { campo: 'label',  messaggio: 'Obbligatorio' },
  colata_tipi_intervento_label_ck: { campo: 'label',  messaggio: 'Obbligatorio' },
  colata_magazzini_label_ck:       { campo: 'label',  messaggio: 'Obbligatorio' },
  colata_unita_misura_label_ck:    { campo: 'label',  messaggio: 'Obbligatorio' },
  colata_ubicazioni_codice_ck:     { campo: 'codice', messaggio: 'Obbligatorio' },
  colata_zone_codice_ck:           { campo: 'codice', messaggio: 'Obbligatorio' },
  colata_zone_nome_ck:             { campo: 'nome',   messaggio: 'Obbligatorio' },
  colata_impianti_nome_ck:         { campo: 'nome',   messaggio: 'Obbligatorio' },
  colata_linee_nome_ck:            { campo: 'nome',   messaggio: 'Obbligatorio' },
  colata_componenti_impianto_fk:   { campo: 'impianto_id', messaggio: 'L\'impianto scelto non appartiene alla zona selezionata.' },
  colata_articoli_codice_ck:       { campo: 'codice', messaggio: 'Codice non valido: 4-24 caratteri, solo lettere, numeri e trattino.' },
  colata_campi_opzioni_ck:         { campo: 'opzioni', messaggio: 'Le opzioni non sono valide: servono chiave ed etichetta, senza duplicati.' },
  colata_campi_tipo_ck:            { campo: 'tipo',   messaggio: 'Tipo di campo non valido.' },
  colata_unita_misura_decimali_ck: { campo: 'decimali', messaggio: 'I decimali devono essere tra 0 e 3.' },
  colata_tipi_intervento_mov_ck:   { campo: 'tipo_movimento', messaggio: 'Comportamento non valido.' },
};

const estraiVincolo = (testo) => {
  const m = /(?:constraint|indice univoco) "([^"]+)"/.exec(testo || '');
  return m ? m[1] : null;
};

// "campo obbligatorio: diametro" -> 'attributi.diametro' (per segnalare l'errore sul campo giusto)
const estraiCampoAttributo = (detail) => {
  const m = /(?:campo obbligatorio|valore non valido per|campo sconosciuto|campo disattivato):?\s*([a-z0-9_]+)/.exec(detail || '');
  return m ? `attributi.${m[1]}` : null;
};

/**
 * Converte QUALSIASI errore (codice applicativo, errore Postgres 23505/23503/..., rete)
 * in un ColataError con messaggio italiano. Unico punto di mappatura.
 */
export const mapColataError = (err) => {
  if (err instanceof ColataError) return err;

  const msg = String(err?.message ?? '');
  const detail = err?.details ?? err?.detail ?? null;
  const sqlstate = err?.code;

  // 1) codice applicativo sollevato dalle funzioni/trigger: message == CODICE
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(msg)) {
    const base = MESSAGGI_CODICE[msg] ?? `Errore: ${msg}`;
    const campo = msg === 'ATTRIBUTI_NON_VALIDI' ? estraiCampoAttributo(detail) : null;
    const dettaglio = msg === 'ATTRIBUTI_NON_VALIDI' && detail ? `${base} (${detail})` : base;
    return new ColataError({ code: msg, message: dettaglio, campo, detail, originale: err });
  }

  // 2) errori Postgres
  if (sqlstate === '23505') {
    const v = VINCOLI[estraiVincolo(msg)];
    if (v) return new ColataError({ code: 'DUPLICATO', message: v.messaggio, campo: v.campo, detail, originale: err });
    return new ColataError({ code: 'DUPLICATO', message: 'Esiste già un elemento con questi valori.', detail, originale: err });
  }

  if (sqlstate === '23503') {
    // cancellazione/modifica di una riga referenziata altrove
    if (/update or delete on table/i.test(msg)) {
      return new ColataError({ code: 'RECORD_IN_USO', message: MESSAGGI_CODICE.RECORD_IN_USO, detail, originale: err });
    }
    return new ColataError({ code: 'RIFERIMENTO_NON_VALIDO', message: MESSAGGI_CODICE.RIFERIMENTO_NON_VALIDO, detail, originale: err });
  }

  if (sqlstate === '23514') {
    const v = VINCOLI[estraiVincolo(msg)];
    return new ColataError({
      code: 'VALORE_NON_VALIDO',
      message: v ? v.messaggio : 'Valore non valido.',
      campo: v ? v.campo : null,
      detail,
      originale: err,
    });
  }

  if (sqlstate === '23502') {
    const m = /column "([^"]+)"/.exec(msg);
    return new ColataError({
      code: 'CAMPO_OBBLIGATORIO',
      message: 'Campo obbligatorio.',
      campo: m ? m[1] : null,
      detail,
      originale: err,
    });
  }

  // permessi negati dal database (RLS / privilegi): per Colata significa nessun diritto di scrittura
  if (sqlstate === '42501' || /row-level security|permission denied/i.test(msg)) {
    return new ColataError({ code: 'SOLA_LETTURA', message: MESSAGGI_CODICE.SOLA_LETTURA, detail, originale: err });
  }

  if (sqlstate === 'PGRST301' || /jwt/i.test(msg)) {
    return new ColataError({ code: 'SESSIONE_SCADUTA', message: MESSAGGI_CODICE.SESSIONE_SCADUTA, detail, originale: err });
  }

  if (/failed to fetch|networkerror|load failed/i.test(msg)) {
    return new ColataError({ code: 'RETE', message: MESSAGGI_CODICE.RETE, detail, originale: err });
  }

  return new ColataError({
    code: 'ERRORE',
    message: `Errore imprevisto${msg ? `: ${msg}` : '.'}`,
    detail,
    originale: err,
  });
};

// ─── Entità ──────────────────────────────────────────────────────────────────
// La UI conosce solo le chiavi di questa mappa.

const ENTITA = {
  // zone: si scrive su colata_zone, si legge dalla view (con n_impianti e n_linee)
  zone:       { tabella: 'colata_zone',             vista: 'colata_v_zone',
                ricerca: ['codice', 'nome', 'descrizione'],                                    ordine: { key: 'codice', direction: 'asc' } },
  // impianti e linee appartengono a una zona (filtro zona_id); l'ordine è quello scelto nel form della zona
  impianti:   { tabella: 'colata_impianti',         ricerca: ['nome'],                         ordine: { key: 'ordine', direction: 'asc' } },
  linee:      { tabella: 'colata_linee',            ricerca: ['nome'],                         ordine: { key: 'ordine', direction: 'asc' } },
  componenti: { tabella: 'colata_componenti',       vista: 'colata_v_componenti',
                ricerca: ['zona_codice', 'zona_nome', 'impianto_nome', 'reparto_label', 'etichetta'], ordine: { key: 'zona_codice', direction: 'asc' } },
  reparti:    { tabella: 'colata_reparti',          ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  campi:      { tabella: 'colata_campi',            ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  cause:      { tabella: 'colata_cause',            ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  tipi:       { tabella: 'colata_tipi_intervento',  ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  unita:      { tabella: 'colata_unita_misura',     ricerca: ['key', 'label'],                  ordine: { key: 'key', direction: 'asc' } },
  magazzini:  { tabella: 'colata_magazzini',        ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  ubicazioni: { tabella: 'colata_ubicazioni',       ricerca: ['codice', 'descrizione'],         ordine: { key: 'codice', direction: 'asc' } },
  articoli:   { tabella: 'colata_articoli',         vista: 'colata_v_articoli',
                ricerca: ['codice', 'nome', 'codice_sap', 'codice_fornitore'],                 ordine: { key: 'nome', direction: 'asc' } },
  compatibilita: { tabella: 'colata_compatibilita', vista: 'colata_v_compatibilita',
                ricerca: ['zona_codice', 'impianto_nome', 'reparto_label', 'componente_etichetta'], ordine: { key: 'zona_codice', direction: 'asc' } },
  // view senza colonna "attivo" (registro): niente filtro attivi
  storico:    { vista: 'colata_v_storico', senzaAttivo: true,
                ricerca: ['codice', 'nome', 'utente_nome'],                                    ordine: { key: 'created_at', direction: 'desc' } },
  interventi: { vista: 'colata_v_interventi', senzaAttivo: true,
                ricerca: ['codice', 'nome', 'zona_codice', 'impianto_nome', 'linea_nome'],         ordine: { key: 'data_intervento', direction: 'desc' } },
  audit:      { vista: 'colata_v_audit_qta', senzaAttivo: true, chiaveUnica: 'articolo_id',
                ricerca: ['codice', 'nome'],                                                   ordine: { key: 'nome', direction: 'asc' } },
};

const config = (entita) => {
  const c = ENTITA[entita];
  if (!c) throw new Error(`Entità Colata sconosciuta: ${entita}`);
  return c;
};

// Rimuove i caratteri che romperebbero il filtro .or() di PostgREST
const pulisciRicerca = (testo) => String(testo ?? '').replace(/[%,()*\\]/g, ' ').trim();

// Filtri: valore semplice = uguaglianza; oggetti con operatore:
//   { contiene: v }  la colonna array contiene v      (es. zone_ids)
//   { ilike: testo } la colonna testuale contiene il testo, senza distinguere maiuscole
//   { diverso: v }   la colonna è diversa da v
//   { da, a }        intervallo di date (stringhe YYYY-MM-DD), uno dei due estremi può mancare
const applicaFiltri = (query, filtri) => {
  let q = query;
  Object.entries(filtri || {}).forEach(([chiave, valore]) => {
    if (valore === null || valore === undefined || valore === '') return;
    if (typeof valore === 'object') {
      if (valore.da !== undefined || valore.a !== undefined) {
        // intervallo di date (YYYY-MM-DD), estremi inclusi
        if (valore.da) q = q.gte(chiave, `${valore.da}T00:00:00`);
        if (valore.a) q = q.lte(chiave, `${valore.a}T23:59:59.999`);
        return;
      }
      if (valore.diverso !== undefined) {
        q = q.neq(chiave, valore.diverso);
        return;
      }
      if (valore.contiene !== undefined && valore.contiene !== '') q = q.contains(chiave, [valore.contiene]);
      else if (valore.ilike !== undefined) {
        const t = pulisciRicerca(valore.ilike);
        if (t) q = q.ilike(chiave, `%${t}%`);
      }
      return;
    }
    q = q.eq(chiave, valore);
  });
  return q;
};

// ─── Permessi ────────────────────────────────────────────────────────────────

/**
 * Permessi dell'utente corrente su Colata (fonte unica: le stesse funzioni usate da RLS).
 * Ritorna { ruolo, leggere, scrivere }.
 */
export const colataPermessi = async () => {
  const { data, error } = await supabase.rpc('colata_permessi');
  if (error) throw mapColataError(error);
  return data ?? { ruolo: null, leggere: false, scrivere: false };
};

// ─── Letture ─────────────────────────────────────────────────────────────────

/**
 * Lista paginata lato server.
 * opzioni: { pagina (0-based), perPagina, sort {key,direction}, ricerca, soloAttivi, filtri {col: valore} }
 * Ritorna { righe, totale }.
 */
export const colataLista = async (entita, opzioni = {}) => {
  const {
    pagina = 0,
    perPagina = 25,
    sort = null,
    ricerca = '',
    soloAttivi = true,
    filtri = {},
  } = opzioni;
  const c = config(entita);
  const da = pagina * perPagina;
  const a = da + perPagina - 1;

  let q = supabase.from(c.vista || c.tabella).select('*', { count: 'exact' });
  if (soloAttivi && !c.senzaAttivo) q = q.eq('attivo', true);
  q = applicaFiltri(q, filtri);

  const termine = pulisciRicerca(ricerca);
  if (termine && c.ricerca?.length) {
    q = q.or(c.ricerca.map((col) => `${col}.ilike.%${termine}%`).join(','));
  }

  const ord = sort?.key ? sort : c.ordine;
  const chiaveUnica = c.chiaveUnica || 'id';
  q = q.order(ord.key, { ascending: ord.direction !== 'desc' });
  if (ord.key !== chiaveUnica) q = q.order(chiaveUnica, { ascending: true });

  const { data, error, count } = await q.range(da, a);
  if (error) throw mapColataError(error);
  return { righe: data ?? [], totale: count ?? 0 };
};

/**
 * Tutte le righe che corrispondono a ricerca/filtri/ordinamento (per l'esportazione),
 * lette a blocchi con .range(). Si ferma a `massimo` righe.
 * Ritorna { righe, totale, troncato }.
 */
export const colataEsporta = async (entita, opzioni = {}, massimo = 10000) => {
  const perPagina = 500;
  let pagina = 0;
  let righe = [];
  let totale = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const res = await colataLista(entita, { ...opzioni, pagina, perPagina });
    totale = res.totale;
    righe = righe.concat(res.righe);
    if (res.righe.length === 0 || righe.length >= totale || righe.length >= massimo) break;
    pagina += 1;
  }
  return { righe: righe.slice(0, massimo), totale, troncato: totale > massimo };
};

/**
 * Elenco per i dropdown (attivi e disattivati: la UI decide cosa mostrare).
 * Limitato a 1000 righe (.range) — i sifarnici Colata sono molto più piccoli.
 */
export const colataOpzioni = async (entita, filtri = {}) => {
  const c = config(entita);
  let q = supabase.from(c.vista || c.tabella).select('*');
  q = applicaFiltri(q, filtri);
  q = q.order(c.ordine.key, { ascending: c.ordine.direction !== 'desc' }).order('id', { ascending: true });
  const { data, error } = await q.range(0, 999);
  if (error) throw mapColataError(error);
  return data ?? [];
};

/** Campi (colata_campi) di un reparto, ordinati. Include i disattivati. */
export const colataCampiReparto = (repartoId) => colataOpzioni('campi', { reparto_id: repartoId });

/** Singolo articolo dalla view (con reparto, unità, ubicazione, stato scorta). null se non esiste. */
export const colataArticolo = async (id) => {
  const { righe } = await colataLista('articoli', { perPagina: 1, soloAttivi: false, filtri: { id } });
  return righe[0] ?? null;
};

// ─── Scritture ───────────────────────────────────────────────────────────────

export const colataCrea = async (entita, payload) => {
  const c = config(entita);
  const { data, error } = await supabase.from(c.tabella).insert(payload).select().single();
  if (error) throw mapColataError(error);
  return data;
};

export const colataAggiorna = async (entita, id, payload) => {
  const c = config(entita);
  const { data, error } = await supabase.from(c.tabella).update(payload).eq('id', id).select().maybeSingle();
  if (error) throw mapColataError(error);
  if (!data) throw new ColataError({ code: 'NESSUN_RECORD', message: MESSAGGI_CODICE.NESSUN_RECORD });
  return data;
};

export const colataImpostaAttivo = (entita, id, attivo) => colataAggiorna(entita, id, { attivo });

/**
 * Salva l'elenco di impianti o linee di una zona ('impianti' | 'linee'), una riga per volta.
 * righe: [{ id?, nome, attivo, originale? }] nell'ordine scelto dall'utente (diventa la colonna "ordine").
 * Le righe nuove si creano, quelle esistenti si aggiornano solo se sono cambiate (nome, ordine, attivo).
 * Un errore su una riga NON ferma le altre: ritorna [{ indice, ok, id, salvato, errore? }].
 */
export const colataSalvaElencoZona = async (entita, zonaId, righe) => {
  const esiti = [];
  for (let i = 0; i < righe.length; i += 1) {
    const r = righe[i];
    const payload = { nome: String(r.nome ?? '').trim(), ordine: i, attivo: r.attivo !== false };
    try {
      let id = r.id ?? null;
      if (r.id) {
        const o = r.originale;
        const cambiato = !o || o.nome !== payload.nome || o.ordine !== payload.ordine || o.attivo !== payload.attivo;
        // eslint-disable-next-line no-await-in-loop
        if (cambiato) await colataAggiorna(entita, r.id, payload);
      } else {
        // eslint-disable-next-line no-await-in-loop
        const creato = await colataCrea(entita, { ...payload, zona_id: zonaId });
        id = creato.id;
      }
      esiti.push({ indice: i, ok: true, id, salvato: payload });
    } catch (e) {
      esiti.push({ indice: i, ok: false, errore: mapColataError(e).message });
    }
  }
  return esiti;
};

/** Cancellazione tramite funzione SQL: se l'elemento è collegato ad altri dati -> RECORD_IN_USO. */
export const colataElimina = async (entita, id) => {
  const c = config(entita);
  const { data, error } = await supabase.rpc('colata_elimina_record', { p_tabella: c.tabella, p_id: id });
  if (error) throw mapColataError(error);
  return data;
};

// ─── Codice articolo ─────────────────────────────────────────────────────────

/** Controllo di comodo lato client (4-24 caratteri: lettere, numeri, trattino). L'autorità resta il database. */
export const codiceValido = (codice) => /^[A-Za-z0-9-]{4,24}$/.test(String(codice ?? ''));

/** Codice interno "COL-000123" mai ripetuto, generato dal database. */
export const colataGeneraCodice = async () => {
  const { data, error } = await supabase.rpc('colata_genera_codice_interno');
  if (error) throw mapColataError(error);
  return data;
};

// ─── Articoli e giacenza ─────────────────────────────────────────────────────

/** Nuovo UUID per l'idempotenza: un solo valore per apertura di modal (doppio click = stessa richiesta). */
export const nuovaChiaveIdempotenza = () => crypto.randomUUID();

/** Crea un articolo con giacenza 0 (la giacenza iniziale si registra con colataRegistraCarico). */
export const colataCreaArticolo = async ({
  codice, nome, repartoId, unitaId, qtaMin = 0, attributi = {},
  codiceSap = null, codiceFornitore = null, descrizione = null, ubicazioneId = null, note = null,
}) => {
  const { data, error } = await supabase.rpc('colata_crea_articolo', {
    p_codice: codice,
    p_nome: nome,
    p_reparto_id: repartoId,
    p_unita_id: unitaId,
    p_qta_min: qtaMin,
    p_attributi: attributi,
    p_codice_sap: codiceSap,
    p_codice_fornitore: codiceFornitore,
    p_descrizione: descrizione,
    p_ubicazione_id: ubicazioneId,
    p_note: note,
  });
  if (error) throw mapColataError(error);
  return data; // { id, codice }
};

/** Anagrafica articolo (mai la giacenza: quella cambia solo con i movimenti). */
export const colataAggiornaArticolo = (id, payload) => {
  const { qta, ...senzaQta } = payload;
  return colataAggiorna('articoli', id, senzaQta);
};

export const colataRegistraCarico = async ({ chiave, articoloId, qta, motivo = null, ordineId = null }) => {
  const { data, error } = await supabase.rpc('colata_registra_carico', {
    p_idempotency_key: chiave,
    p_articolo_id: articoloId,
    p_qta: qta,
    p_motivo: motivo,
    p_intervento_ordine_id: ordineId,
  });
  if (error) throw mapColataError(error);
  return data;
};

export const colataRegistraRettifica = async ({ chiave, articoloId, delta, motivo }) => {
  const { data, error } = await supabase.rpc('colata_registra_rettifica', {
    p_idempotency_key: chiave,
    p_articolo_id: articoloId,
    p_qta_delta: delta,
    p_motivo: motivo,
  });
  if (error) throw mapColataError(error);
  return data;
};

/**
 * Correzione diretta di giacenza: NON compare nello Storico e non richiede nota
 * (movimento di tipo 'correzione': conta nella somma dei movimenti, quindi l'audit resta coerente).
 */
export const colataRegistraCorrezione = async ({ chiave, articoloId, delta }) => {
  const { data, error } = await supabase.rpc('colata_registra_correzione', {
    p_idempotency_key: chiave,
    p_articolo_id: articoloId,
    p_qta_delta: delta,
  });
  if (error) throw mapColataError(error);
  return data;
};

// ─── Interventi ──────────────────────────────────────────────────────────────

/**
 * Registra un intervento (Sostituzione = scarico, Rientrato = carico, Ordinato = nessuna variazione)
 * in un'unica transazione. Ritorna { intervento_id, movimento_id, qta, qta_prima, stato, ordine_id, ordine_stato, duplicato }.
 */
export const colataRegistraIntervento = async ({
  chiave, tipoId, zonaId, articoloId, qta, componenteId = null, causaId = null,
  dataIntervento = null, note = null, consegnaPrevista = null, ordineId = null,
  impiantoId = null, lineaId = null,
}) => {
  const { data, error } = await supabase.rpc('colata_registra_intervento', {
    p_idempotency_key: chiave,
    p_tipo_id: tipoId,
    p_zona_id: zonaId,
    p_articolo_id: articoloId,
    p_qta: qta,
    p_componente_id: componenteId,
    p_causa_id: causaId,
    p_data_intervento: dataIntervento,
    p_note: note,
    p_data_consegna_prevista: consegnaPrevista,
    p_ordine_id: ordineId, // solo per i Rientrati: ordine aperto da chiudere (in tutto o in parte) con questo rientro
    p_impianto_id: impiantoId, // obbligatorio se la zona ha impianti attivi (con un componente si ricava da lui)
    p_linea_id: lineaId, // facoltativa: solo per le zone che hanno linee
  });
  if (error) throw mapColataError(error);
  return data;
};

/** Cambia la data di consegna prevista di un ordine aperto oppure lo annulla (se non ha ricezioni). */
export const colataAggiornaOrdine = async ({ interventoId, consegnaPrevista = null, annulla = false }) => {
  const { data, error } = await supabase.rpc('colata_aggiorna_ordine', {
    p_intervento_id: interventoId,
    p_data_consegna_prevista: consegnaPrevista,
    p_annulla: annulla,
  });
  if (error) throw mapColataError(error);
  return data;
};

/**
 * Storno di un movimento (unico modo di correggere lo Storico, che è append-only):
 * scrive un movimento opposto collegato. Sostituzione/Rientrato annullano anche l'intervento;
 * un Carico di un ordine annulla la ricezione (l'ordine si riapre). Un movimento si storna una sola volta.
 */
export const colataStornaMovimento = async ({ chiave, movimentoId, motivo }) => {
  const { data, error } = await supabase.rpc('colata_storna_movimento', {
    p_idempotency_key: chiave,
    p_movimento_id: movimentoId,
    p_motivo: motivo,
  });
  if (error) throw mapColataError(error);
  return data;
};

/**
 * Modifica di un intervento in una sola transazione.
 *  - modalita 'semplice': nessun nuovo movimento (zona, impianto, linea, componente, causa, nota, data; per un ordine aperto anche articolo/quantità);
 *  - modalita 'storno': storno del movimento originale + nuovo intervento con nuovo movimento (l'originale diventa Annullato);
 *  - modalita 'nessuna_modifica': nulla è cambiato.
 */
export const colataModificaIntervento = async ({
  chiave, interventoId, tipoId, zonaId, articoloId, qta, componenteId = null, causaId = null,
  dataIntervento = null, note = null, consegnaPrevista = null, impiantoId = null, lineaId = null,
}) => {
  const { data, error } = await supabase.rpc('colata_modifica_intervento', {
    p_idempotency_key: chiave,
    p_intervento_id: interventoId,
    p_tipo_id: tipoId,
    p_zona_id: zonaId,
    p_articolo_id: articoloId,
    p_qta: qta,
    p_componente_id: componenteId,
    p_causa_id: causaId,
    p_data_intervento: dataIntervento,
    p_note: note,
    p_data_consegna_prevista: consegnaPrevista,
    p_impianto_id: impiantoId,
    p_linea_id: lineaId,
  });
  if (error) throw mapColataError(error);
  return data;
};

/** "Elimina" un intervento: storno del movimento (giacenza ripristinata) e intervento Annullato. Nulla viene cancellato. */
export const colataAnnullaIntervento = async ({ chiave, interventoId }) => {
  const { data, error } = await supabase.rpc('colata_annulla_intervento', {
    p_idempotency_key: chiave,
    p_intervento_id: interventoId,
  });
  if (error) throw mapColataError(error);
  return data;
};

/**
 * Chiude un ordine aperto caricando in magazzino il residuo (movimento Carico collegato all'ordine,
 * giacenza aumentata, riga nello Storico): ordinato = ricevuto. Ritorna { intervento_id, stato, qta_ricevuta, qta_caricata, qta }.
 */
export const colataSegnaOrdineRicevuto = async ({ interventoId }) => {
  const { data, error } = await supabase.rpc('colata_segna_ordine_ricevuto', {
    p_intervento_id: interventoId,
  });
  if (error) throw mapColataError(error);
  return data;
};

// ─── Statistiche ─────────────────────────────────────────────────────────────

/**
 * Dati aggregati per le statistiche: una riga per mese × zona × impianto × linea × reparto × causa × tipo × articolo
 * (esclusi gli interventi annullati). Letti a blocchi di 1000 (.range) con ordinamento totale,
 * così nessuna riga viene saltata o ripetuta tra una pagina e l'altra.
 * Ritorna { righe, troncato }.
 */
export const colataStatistiche = async (massimoPagine = 30) => {
  const perPagina = 1000;
  let righe = [];
  for (let pagina = 0; pagina < massimoPagine; pagina += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await supabase
      .from('colata_v_stat_mensile')
      .select('mese, anno, mese_num, zona_id, zona_codice, reparto_id, reparto_label, causa_id, causa_label, tipo_id, tipo_key, articolo_id, codice, nome, n_interventi, qta_totale, impianto_id, impianto_nome, linea_id, linea_nome')
      .order('mese', { ascending: true })
      .order('zona_id', { ascending: true })
      .order('reparto_id', { ascending: true })
      .order('causa_id', { ascending: true, nullsFirst: true })
      .order('tipo_id', { ascending: true })
      .order('articolo_id', { ascending: true })
      .order('impianto_id', { ascending: true, nullsFirst: true })
      .order('linea_id', { ascending: true, nullsFirst: true })
      .range(pagina * perPagina, pagina * perPagina + perPagina - 1);
    if (error) throw mapColataError(error);
    righe = righe.concat(data ?? []);
    if (!data || data.length < perPagina) return { righe, troncato: false };
  }
  return { righe, troncato: true };
};
